import { useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { initFirebase } from '@/lib/firebase';
import { syncNow } from '@/lib/sync';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export default function AuthStatus() {
  const [ready, setReady] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Hooks must not be called conditionally; declare before early returns
  const [open, setOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const ctx = await initFirebase();
      if (!ctx) return;
      const auth = getAuth();
      onAuthStateChanged(auth, (u) => {
        setUserEmail(u?.email || null);
        setReady(true);
      });
    })();
  }, []);

  async function loginOrRegister(kind: 'login' | 'register') {
    setError(null);
    try {
      const auth = getAuth();
      if (kind === 'register' && password.length < 6) {
        setError('パスワードは6文字以上にしてください');
        return;
      }
      if (kind === 'login') await signInWithEmailAndPassword(auth, email, password);
      else await createUserWithEmailAndPassword(auth, email, password);
      setEmail(''); setPassword('');
    } catch (e: unknown) {
      const code = e?.code ? String(e.code) : '';
      setError((code ? code + ': ' : '') + (e?.message || '認証に失敗しました'));
    }
  }

  async function manualSync() {
    setSyncMsg(null);
    setSyncing(true);
    try {
      const ok = await syncNow();
      setSyncMsg(ok ? '同期しました' : '同期できません（未ログイン？）');
    } catch {
      setSyncMsg('同期に失敗しました');
    } finally {
      setTimeout(() => setSyncMsg(null), 2500);
      setSyncing(false);
    }
  }

  if (!ready) return null;

  // モバイル（sm未満）はダイアログで表示、sm以上はインライン表示
  const Inline = (
    <div className="hidden sm:flex items-center gap-2">
      {userEmail ? (
        <>
          <Button size="sm" variant="secondary" disabled={syncing} onClick={manualSync}>
            {syncing ? '同期中' : (syncMsg ?? '同期')}
          </Button>
          <span className="text-sm text-muted-foreground max-w-[12rem] truncate">{userEmail}</span>
          <Button size="sm" variant="outline" onClick={() => signOut(getAuth())}>ログアウト</Button>
        </>
      ) : (
        <>
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="メールアドレス" className="h-8 w-40" />
          <Input value={password} type="password" onChange={(e) => setPassword(e.target.value)} placeholder="パスワード" className="h-8 w-32" />
          <Button size="sm" onClick={() => loginOrRegister('login')}>ログイン</Button>
          <Button size="sm" variant="outline" onClick={() => loginOrRegister('register')}>新規</Button>
          {error && <span className="text-xs text-red-600">{error}</span>}
        </>
      )}
    </div>
  );

  const Mobile = (
    <div className="sm:hidden">
      <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
        {userEmail ? 'アカウント' : 'ログイン'}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm p-4">
          <DialogHeader>
            <DialogTitle>{userEmail ? 'アカウント' : 'ログイン / 新規登録'}</DialogTitle>
          </DialogHeader>
          {userEmail ? (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground break-all">{userEmail}</div>
              <Button className="w-full" variant="secondary" disabled={syncing} onClick={manualSync}>
                {syncing ? '同期中' : (syncMsg ?? '同期する')}
              </Button>
              <Button className="w-full" variant="outline" onClick={() => { signOut(getAuth()); setOpen(false); }}>
                ログアウト
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="メールアドレス" inputMode="email" autoComplete="email" />
              <Input value={password} type="password" onChange={(e) => setPassword(e.target.value)} placeholder="パスワード（6文字以上）" autoComplete="new-password" />
              {error && <div className="text-xs text-red-600">{error}</div>}
              <div className="flex gap-2">
                <Button className="flex-1" onClick={() => loginOrRegister('login')}>ログイン</Button>
                <Button className="flex-1" variant="outline" onClick={() => loginOrRegister('register')}>新規</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );

  return (
    <div className="flex items-center gap-2">
      {Inline}
      {Mobile}
    </div>
  );
}
